import { Component, Input, Output, EventEmitter } from '@angular/core';
import { IKBLearnedSubSets, IKBLearnedWithDetails } from 'app/base-knowledge-catalog/interfaces/IBase-knowledge-catalog';
import { I18nService } from 'i18n/i18n.service';

@Component({
  selector: 'bizagi-data-detail',
  templateUrl: './bizagi-data-detail.component.html',
  styleUrls: ['./bizagi-data-detail.component.scss']
})
export class BizagiDataDetailComponent {
  
  @Input() selectedItem: any = null; // previously selectedTask
  @Input() kbLearnedDetails: IKBLearnedWithDetails[] = []; // list of entity details
  @Input() isLoadingDetails: boolean = false;
  @Output() refreshItem = new EventEmitter<any>();
  
  readonly defaultSubSetName: string = 'all_records';
  isDeletedWarningClosed: boolean = false;

  constructor(private _i18nService: I18nService) { }

  trackByEntity = (_: number, entity: IKBLearnedWithDetails) => entity?.Id;
  trackBySubset = (_: number, subset: IKBLearnedSubSets) => subset?.Id;

  shouldShowNoSubsetsForBizagiData(entity: any): boolean {
    return !!entity?.HasNoRelatedSubsetsAfterSchedulerExecution;
  }

  isDeletedAllRecordsSubset(subset: any): boolean {
    return !!subset?.IsDeleted && String(subset?.DisplayName || '').toLowerCase() === this.defaultSubSetName;
  }

  shouldHideDeletedAllRecordsPlaceholder(entity: any): boolean {
    if (entity?.HasNoRelatedSubsetsAfterSchedulerExecution) {
      return false;
    }

    const subsets = entity?.SubSets || [];
    const hasRealSubsets = subsets.some((subset: any) => !subset?.IsDeleted);
    return hasRealSubsets;
  }

  getVisibleSubsets(entity: any): any[] {
    const subsets = entity?.SubSets || [];

    if (!this.shouldHideDeletedAllRecordsPlaceholder(entity)) {
      return subsets;
    }

    return subsets.filter((subset: any) => !this.isDeletedAllRecordsSubset(subset));
  }

  getVisibleCompletedProgress(entity: any): string {
    const visibleSubsets = this.getVisibleSubsets(entity);
    const completedCount = visibleSubsets.filter((subset: any) => this.mapStatus(subset?.Status) === 'COMPLETED').length;
    return `${completedCount}/${visibleSubsets.length}`;
  }

  mapStatus(status: any): string {
    if (!status) return '';
    return String(status).toUpperCase();
  }

  getFrecuencyTranslated(frecuency: any): string {
    return this._i18nService.instant('BASEKNOWLEDGECATALOG.' + String(frecuency).toUpperCase());
  }

  getStatusTranslated(status: any): string {
    return this._i18nService.instant('BASEKNOWLEDGECATALOG.STATUS.' + this.mapStatus(status));
  }

  onRefreshEntity(entity: any, event?: Event) {
    if (event) { event.stopPropagation(); }
    this.refreshItem.emit(this.selectedItem);
  }

  statusSubSetColors(status: any): string {
    const statusUpper = this.mapStatus(status);
    switch (statusUpper) {
      case 'COMPLETED':
      case 'DELIVERED':
        return 'status-subset-completed';
      case 'FAILED':
        return 'status-subset-failed';
      case 'INPROGRESS':
        return 'status-subset-inprogress';
      case 'AWAITING':
        return 'status-subset-awaiting';
      case 'DATANOTFOUND':
        return 'status-subset-datanotfound';
      case 'NOTINITIATED':
      case 'NOTEXECUTED':
      case 'DELETED':
      case 'MODIFIED':
      default:
        return 'status-subset-default';
    }
  }

  hasFailedSubsets(): boolean {
    if (!this.kbLearnedDetails || this.kbLearnedDetails.length === 0) {
      return false;
    }

    return this.kbLearnedDetails.some((entity: any) => {
      const subsets = entity?.SubSets || [];
      const hasFailedSubset = subsets.some((subset: any) => this.mapStatus(subset.Status) === 'FAILED');
      const hasFailedEntityWithoutSubsets = this.mapStatus(entity?.Status) === 'FAILED' && subsets.length === 0;

      return hasFailedSubset || hasFailedEntityWithoutSubsets;
    });
  }

  hasSchedulerNoSubsetsFailedScenario(): boolean {
    if (!this.kbLearnedDetails || this.kbLearnedDetails.length === 0) {
      return false;
    }

    return this.kbLearnedDetails.some((entity: any) => this.shouldShowRetryButtonOnlyForSchedulerScenario(entity));
  }

  getRetryWarningTitleKey(): string {
    if (this.hasSchedulerNoSubsetsFailedScenario()) {
      return 'BASEKNOWLEDGECATALOG.SUBSET.RETRY_NO_SUBSETS_TITLE';
    }
    return 'BASEKNOWLEDGECATALOG.SUBSET.RETRY_TITLE';
  }

  getRetryWarningMessageKey(): string {
    if (this.hasSchedulerNoSubsetsFailedScenario()) {
      return 'BASEKNOWLEDGECATALOG.SUBSET.RETRY_NO_SUBSETS_MESSAGE';
    }
    return 'BASEKNOWLEDGECATALOG.SUBSET.RETRY_MESSAGE';
  }

  shouldShowRetryButtonOnlyForSchedulerScenario(entity: any): boolean {
    if (!entity?.HasNoRelatedSubsetsAfterSchedulerExecution) {
      return false;
    }

    const subsets = entity?.SubSets || [];
    return subsets.some((subset: any) => this.mapStatus(subset.Status) === 'FAILED');
  }

  shouldShowNoDataMessageOnlyForSchedulerScenario(entity: any): boolean {
    if (!entity?.HasNoRelatedSubsetsAfterSchedulerExecution) {
      return false;
    }

    const subsets = entity?.SubSets || [];
    return subsets.some((subset: any) => this.mapStatus(subset.Status) === 'DATANOTFOUND');
  }

  getNoSubsetsMessage(entity: any): string {
    if (!entity?.HasNoRelatedSubsetsAfterSchedulerExecution) {
      return 'BASEKNOWLEDGECATALOG.SUBSET.NO_RECORDS';
    }
    if (this.shouldShowRetryButtonOnlyForSchedulerScenario(entity)) {
      return 'BASEKNOWLEDGECATALOG.SUBSET.SCHEDULER_EXECUTED_FAILED';
    }
    if (this.shouldShowNoDataMessageOnlyForSchedulerScenario(entity)) {
      return 'BASEKNOWLEDGECATALOG.SUBSET.SCHEDULER_EXECUTED_NO_DATA';
    }
    return 'BASEKNOWLEDGECATALOG.SUBSET.SCHEDULER_EXECUTED_SUCCESS';
  }

  hasDeletedSubsets(): boolean {
    if (!this.kbLearnedDetails || this.kbLearnedDetails.length === 0) {
      return false;
    }
    
    return this.kbLearnedDetails.some((entity: any) => {
      const visibleSubsets = this.getVisibleSubsets(entity);
      return visibleSubsets.some((subset: any) => subset.IsDeleted && !entity?.HasNoRelatedSubsetsAfterSchedulerExecution);
    });
  }

  onCloseDeletedWarning(): void {
    this.isDeletedWarningClosed = true;
  }

  shouldShowDataNotFoundMessage(): boolean {
    if (!this.kbLearnedDetails || this.kbLearnedDetails.length === 0) {
      return false;
    }

    // Verificar si NO hay subsets fallidos (no eliminados)
    const hasFailedSubsets = this.kbLearnedDetails.some((entity: any) => 
      entity.SubSets && entity.SubSets.some((subset: any) => 
        !subset.IsDeleted && !entity?.HasNoRelatedSubsetsAfterSchedulerExecution && this.mapStatus(subset.Status) === 'FAILED'
      )
    );

    // Si hay fallidos, no mostrar el mensaje de DataNotFound
    if (hasFailedSubsets) {
      return false;
    }

    // Verificar si hay subsets con DataNotFound (no eliminados)
    const hasDataNotFound = this.kbLearnedDetails.some((entity: any) => 
      entity.SubSets && entity.SubSets.some((subset: any) => 
        !subset.IsDeleted && !entity?.HasNoRelatedSubsetsAfterSchedulerExecution && this.mapStatus(subset.Status) === 'DATANOTFOUND'
      )
    );

    // Verificar si hay DataNotFound en entidades sin subsets
    const hasDataNotFoundNoSubsets = this.kbLearnedDetails.some((entity: any) => 
      entity?.HasNoRelatedSubsetsAfterSchedulerExecution && 
      entity?.SubSets && entity.SubSets.some((subset: any) => this.mapStatus(subset.Status) === 'DATANOTFOUND')
    );

    return hasDataNotFound || hasDataNotFoundNoSubsets;
  }
}
